/**
 * GroupLiveAggregate — Anonymous group-level results view.
 *
 * VER-56 Model A: Shows aggregate WEMWBS scores after a session closes.
 *
 * ANONYMITY CONTRACT (non-negotiable — see docs/platform-upgrade-2026.md § Safety):
 * - Individual scores are NEVER shown, even to the practitioner.
 * - Only group mean and band-count distribution are displayed.
 * - If fewer than 3 participants submitted, the aggregate is suppressed entirely
 *   (to prevent re-identification by exclusion). This is enforced both here
 *   (UI guard) and MUST be enforced server-side (TODO [VER-55]).
 * - Band counts are shown as counts only — never as percentages that would reveal
 *   individual answers when group size is small.
 *
 * MOCK-FIRST: Uses MOCK_COMPLETE_SESSION.aggregate as the data source.
 *
 * TODOs:
 * - TODO [VER-55]: Replace mock aggregate with GET /api/sessions/:id/aggregate
 *   (authenticated, practitioner-only). Response shape: GroupAggregateSummary.
 * - TODO [VER-55]: The server MUST suppress individual scores. Client-side
 *   suppression here is a UX guard only — not a security boundary.
 * - TODO [RBAC]: Guard with the correct permit code once defined.
 * - TODO [WEMWBS]: Run verbal-wemwbs-license-check before showing any WEMWBS
 *   score labels to confirm licence allows practitioner display.
 */

import React from "react";
import { Alert, Box, Card, CardContent, Chip, Divider, Grid, LinearProgress, Typography } from "@mui/material";
import { BarChartOutlined, CheckCircleOutlined, GroupOutlined, WarningAmberOutlined } from "@mui/icons-material";
import { GroupAggregateSummary, GroupLiveSession } from "./groupLive.mock";

// ---------------------------------------------------------------------------
// Anonymity threshold — suppress aggregate below this submission count
// ---------------------------------------------------------------------------

const MIN_SUBMISSIONS_FOR_AGGREGATE = 3;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type GroupLiveAggregateProps = {
    session: GroupLiveSession;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

type StatCardProps = {
    label: string;
    value: string | number;
    icon: React.ReactElement;
};

const StatCard = ({ label, value, icon }: StatCardProps): JSX.Element => (
    <Card>
        <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
                {icon}
                <Typography variant="body2" color="textSecondary">
                    {label}
                </Typography>
            </Box>
            <Typography variant="h3" color="primary">
                {value}
            </Typography>
        </CardContent>
    </Card>
);

type BandBarProps = {
    label: string;
    count: number;
    total: number;
    color: "success" | "warning" | "error";
};

const BandBar = ({ label, count, total, color }: BandBarProps): JSX.Element => {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <Box mb={2}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" fontWeight={700}>
                    {label}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {count} participant{count !== 1 ? "s" : ""}
                </Typography>
            </Box>
            <LinearProgress variant="determinate" value={pct} color={color} sx={{ borderRadius: 2, height: 10 }} />
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Aggregate display
// ---------------------------------------------------------------------------

type AggregatePanelProps = {
    aggregate: GroupAggregateSummary;
};

const AggregatePanel = ({ aggregate }: AggregatePanelProps): JSX.Element => {
    const { submitted, bandCounts, meanScore } = aggregate;

    // Anonymity guard: suppress if too few submissions
    if (submitted < MIN_SUBMISSIONS_FOR_AGGREGATE) {
        return (
            <Alert severity="warning" icon={<WarningAmberOutlined />}>
                Fewer than {MIN_SUBMISSIONS_FOR_AGGREGATE} participants submitted. Group results are not shown to
                protect individual anonymity.
            </Alert>
        );
    }

    return (
        <>
            {/* Mean score */}
            <Box mb={4}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <StatCard
                            label="Group mean WEMWBS score"
                            value={meanScore !== null ? meanScore.toFixed(1) : "—"}
                            icon={<BarChartOutlined color="primary" />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <StatCard
                            label="Participants submitted"
                            value={submitted}
                            icon={<CheckCircleOutlined color="success" />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <StatCard
                            label="Total invited"
                            value={aggregate.totalInvited}
                            icon={<GroupOutlined color="action" />}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Score band distribution */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" mb={1}>
                        Wellbeing band distribution
                    </Typography>
                    <Typography variant="body2" color="textSecondary" mb={3}>
                        WEMWBS total score bands. Counts only — no individual breakdown.
                    </Typography>
                    <BandBar label="High wellbeing (53–70)" count={bandCounts.high} total={submitted} color="success" />
                    <BandBar
                        label="Moderate wellbeing (35–52)"
                        count={bandCounts.moderate}
                        total={submitted}
                        color="warning"
                    />
                    <BandBar label="Low wellbeing (14–34)" count={bandCounts.low} total={submitted} color="error" />
                </CardContent>
            </Card>

            {/* WEMWBS licence / clinical notice */}
            <Alert severity="warning">
                WEMWBS scores are wellbeing indicators only — not clinical diagnoses. Do not share individual scores
                with participants or use this data to make referral decisions without appropriate assessment.{" "}
                {/* TODO [WEMWBS]: Run verbal-wemwbs-license-check before adding any further score labels. */}
            </Alert>
        </>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const GroupLiveAggregate = ({ session }: GroupLiveAggregateProps): JSX.Element => {
    const submitted = session.participants.filter((p) => p.status === "submitted").length;

    return (
        <Box p={4} width={1}>
            {/* Header */}
            <Box mb={4} display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="h4" color="textPrimary" gutterBottom>
                        Group results
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        {session.name}
                    </Typography>
                </Box>
                <Chip label="Session complete" color="success" icon={<CheckCircleOutlined />} />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Anonymity notice — always visible */}
            <Alert severity="info" sx={{ mb: 3 }}>
                Only anonymous group-level results are shown. Individual participant responses cannot be accessed from
                this view.
            </Alert>

            {/* Aggregate panel or placeholder */}
            {session.aggregate !== null ? (
                <AggregatePanel aggregate={session.aggregate} />
            ) : submitted < MIN_SUBMISSIONS_FOR_AGGREGATE ? (
                <Alert severity="warning">
                    Fewer than {MIN_SUBMISSIONS_FOR_AGGREGATE} participants submitted. Group results are not available
                    for this session.
                </Alert>
            ) : (
                <Alert severity="info">
                    {/* TODO [VER-55]: Aggregate is computed server-side on session close. */}
                    Aggregate results are being computed. Refresh in a moment.
                </Alert>
            )}
        </Box>
    );
};

export default GroupLiveAggregate;
